import { LoopStep } from "app/singing/vocal-driller/usePlayer/loopSteps";
import classNames from "classnames";
import { useEffect, useRef } from "react";
import { purple, red } from "tailwindcss/colors";
import { useRefMeasure } from "util/useMeasure";
import {
  StaveNote,
  Renderer,
  Stave,
  Voice,
  Formatter,
  Annotation,
  Dot,
  AnnotationVerticalJustify,
} from "vexflow";
import loopStepsToVexFlow from "./loopStepsToVexFlow";

import "./VexFlow.css";

interface Props {
  steps: LoopStep[];
  error?: boolean;
}

const padding = {
  stav: 10,
  innerStav: 10,
};

const backgroundClass = "bg-black";
const staveFillColor = purple[500];
const noteColor = "white";

export default function VexFlow({ steps, error }: Props) {
  const div = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const renderer = useRef<Renderer>();
  const { height, width } = useRefMeasure({ key: "VexFlow", ref: measureRef });
  const fillStyle = error ? red[500] : staveFillColor;

  function getRenderer() {
    if (!div.current) {
      throw new Error("div.current is null");
    }

    if (!renderer.current) {
      renderer.current = new Renderer(div.current, Renderer.Backends.SVG);

      return renderer.current;
    }

    return renderer.current;
  }

  useEffect(() => {
    const staveWidth = width - padding.stav * 2;
    const stave = new Stave(padding.stav / 2, 0, staveWidth, {
      space_above_staff_ln: 2,
      space_below_staff_ln: 2,
      fill_style: fillStyle,
    });
    const context = getRenderer().getContext();
    const { notes, numOfBeats } = loopStepsToVexFlow({ loopSteps: steps });

    if (notes.length <= 0) {
      context.clear();
      stave.setContext(context).draw();
      return;
    }

    const voice = new Voice({ num_beats: numOfBeats, beat_value: 4 });

    voice.addTickables(
      notes.map((config) => {
        const stavNote = new StaveNote(config);

        stavNote.setStyle({ fillStyle: noteColor });
        stavNote.stem?.setStyle({
          fillStyle: noteColor,
          strokeStyle: noteColor,
        });
        stavNote.addClass("testing ");

        if (config.dots) {
          Dot.buildAndAttach([stavNote], { all: true });
        }

        // Add degree annotation
        const noteAnnotation = new Annotation(config.degrees.join("/"));
        noteAnnotation.setVerticalJustification(
          AnnotationVerticalJustify.BOTTOM
        );

        // TODO: add chords like this
        // stavNote.addModifier(new Annotation("IV Maj"));

        stavNote.addModifier(noteAnnotation);

        return stavNote;
      })
    );

    new Formatter()
      .joinVoices([voice])
      .format([voice], staveWidth - padding.innerStav * 2);

    context.clear();
    stave.setContext(context).draw();
    voice.draw(context, stave);
  }, [steps, width, fillStyle]);

  useEffect(() => {
    getRenderer().resize(width, height);
  }, [height, width]);

  return (
    <div
      ref={measureRef}
      className={classNames(
        "h-24 w-full relative",
        backgroundClass,
        "font-rogan"
      )}
    >
      <div ref={div} className="absolute inset-0" />
    </div>
  );
}
